/**
 * NosAKActionStrip — Per-page executive action directive.
 * Answers: What action is required? Risk? Value at stake? Implementation status?
 * This transforms every page from "information display" into "decision support."
 */
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNosak } from "@/context/NOSAKContext";
import { ArrowRight, AlertTriangle, Zap, DollarSign } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtU = (v) => { if (!v) return "—"; const n = Number(v); if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`; return `$${n}`; };

export default function NosAKActionStrip() {
  const { group, projects } = useNosak();
  const location = useLocation();
  const navigate = useNavigate();

  const qs = group.qualification_summary || {};
  const fin = group.sustainable_finance || {};
  const conf = group.data_confidence_avg || 0;
  const projList = projects?.list || [];
  const finReady = projList.filter(p => ["design","implementation"].includes(p.status));
  const topProject = finReady[0] || projList[0];

  // Per-page action directives — SSOT-derived values
  const ACTIONS = {
    "/nosak/dashboard": {
      urgency: "High", color: "#dc2626", bg: "#fef2f2",
      action: "Commission POME methane baseline study at Nosak Farm Produce",
      risk: "Carbon programme delay — $" + fmtN(qs.monetisable_total * 18) + "/yr at risk",
      value: fmtN(qs.monetisable_total, "tCO₂e/yr") + " monetisable · 12-month lead time",
      cta: "Finance & Carbon →",
      path: "/nosak/finance",
    },
    "/nosak/workspace": {
      urgency: "Medium", color: "#d97706", bg: "#fffbeb",
      action: "Upgrade Distilleries fuel records to T1 measured evidence",
      risk: "Current confidence " + conf.toFixed(0) + "/100 — below 65 VVB threshold",
      value: "Confidence upgrade unlocks ~$" + fmtU(qs.monetisable_total * 18) + "/yr carbon pathway",
      cta: "Evidence Vault →",
      path: "/nosak/evidence",
    },
    "/nosak/intelligence": {
      urgency: "High", color: "#dc2626", bg: "#fef2f2",
      action: "HFO-to-biomass conversion requires board CAPEX decision ($8.5M)",
      risk: "HFO price/supply volatility — highest single Scope 1 risk",
      value: fmtN(group.scope1_tco2e * 0.55, "tCO₂e/yr") + " addressable with full HFO transition",
      cta: "Finance structuring →",
      path: "/nosak/finance",
    },
    "/nosak/finance": {
      urgency: "Medium", color: "#1d4ed8", bg: "#eff6ff",
      action: topProject ? `${topProject.name?.slice(0,45)} — initiate DFI pre-engagement` : "Initiate DFI pre-engagement for green bond programme",
      risk: "Blended finance window conditional on evidence quality and methodology readiness",
      value: fmtU(fin.total_capex_pipeline || 0) + " pipeline · " + (fin.icma_eligible_count || 0) + " ICMA-eligible subs",
      cta: "ESG readiness →",
      path: "/nosak/esg",
    },
    "/nosak/esg": {
      urgency: "Medium", color: "#7c3aed", bg: "#f5f3ff",
      action: "Complete IFRS S2 TCFD disclosure — 4 of 8 items incomplete",
      risk: "Institutional investor ESG requirements intensifying — disclosure gap creates reputational risk",
      value: "Readiness 42% → target 80% before first DFI engagement",
      cta: "Evidence gaps →",
      path: "/nosak/evidence",
    },
    "/nosak/evidence": {
      urgency: "High", color: "#dc2626", bg: "#fef2f2",
      action: "3 subsidiaries at estimated evidence level — upgrade to declared T2 required",
      risk: "VVB validation will fail at <65/100 confidence — carbon programme blocked",
      value: "Evidence upgrade: T1 → +20pt confidence → VCM pathway unlocked",
      cta: "Data workspace →",
      path: "/nosak/workspace",
    },
    "/nosak/settings": {
      urgency: "Low", color: "#64748b", bg: "#f8fafc",
      action: "Schedule quarterly methodology review — Nigeria NERC grid factor update pending",
      risk: "NERC 2022 factor (0.43) may require revision as grid evolves",
      value: "Factor updates affect Scope 2 baseline and reduction potential calculations",
      cta: "View methodology",
      path: "/nosak/settings",
    },
  };

  const act = ACTIONS[location.pathname];
  if (!act) return null;

  const URGENCY_ICON = { High: AlertTriangle, Medium: Zap, Low: DollarSign };
  const Icon = URGENCY_ICON[act.urgency] || Zap;

  return (
    <div
      className="flex items-center justify-between gap-3 px-5 py-2.5 border-b"
      style={{ background: act.bg, borderColor: act.color + "30" }}
      data-testid="action-strip"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Icon size={12} style={{ color: act.color }} />
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: act.color }}>
            {act.urgency} Priority
          </span>
        </div>
        <div className="w-px h-4 bg-slate-200" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-slate-800 truncate">{act.action}</p>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[9px] text-red-600 truncate">{act.risk}</span>
            <span className="text-slate-300">·</span>
            <span className="text-[9px] text-slate-500 truncate hidden sm:block">{act.value}</span>
          </div>
        </div>
      </div>
      <button
        onClick={() => navigate(act.path)}
        className="flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-lg flex-shrink-0 transition-all hover:opacity-80"
        style={{ background: act.color, color: "white" }}
        data-testid="action-cta"
      >
        {act.cta} <ArrowRight size={10} />
      </button>
    </div>
  );
}
